import { UzBadge, UzButton, type UzColumn, UzNumberInput, UzSwitch, UzTable } from '@utazzo/ui';
import { Settings2 } from 'lucide-react';
import React, { useState } from 'react';

import { useNotificationChannels, useUpdateNotificationChannel } from '../../use-notifications';
import { ChannelConfigDrawer } from '../ChannelConfigDrawer';

import type { NotificationChannelItem } from '@utazzo/shared';

/**
 * Channels tab. The channel catalog is seeded on boot. Admins can
 * activate / deactivate a channel and tune its `rateLimitPerSec`
 * throttle. Stubbed channels (sms, push) live alongside
 * the active ones (email, in_app) so admins can flip them on once
 * the implementation lands.
 */
export const NotificationChannelsTab = (): React.JSX.Element => {
  const channelsQuery = useNotificationChannels();
  const updateChannel = useUpdateNotificationChannel();
  const [configuring, setConfiguring] = useState<NotificationChannelItem | null>(null);

  const data = channelsQuery.data?.data ?? [];

  const columns: ReadonlyArray<UzColumn<NotificationChannelItem>> = [
    {
      id: 'code',
      header: 'Channel code',
      cell: (row) => <span className="font-mono text-xs text-zinc-800">{row.code}</span>,
      width: '160px',
    },
    {
      id: 'kind',
      header: 'Kind',
      cell: (row) => (
        <UzBadge tone="secondary" pill>
          {row.kind}
        </UzBadge>
      ),
      width: '110px',
    },
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-zinc-900">{row.name}</span>
          {row.description && <span className="text-xs text-zinc-500">{row.description}</span>}
        </div>
      ),
    },
    {
      id: 'rate',
      header: 'Rate (sec)',
      cell: (row) => <RateCell row={row} />,
      width: '140px',
    },
    {
      id: 'config',
      header: 'Config secret',
      cell: (row) =>
        row.configSecretRef ? (
          <span className="font-mono text-xs text-zinc-700">{row.configSecretRef}</span>
        ) : (
          <span className="text-xs text-zinc-400">-</span>
        ),
      width: '180px',
    },
    {
      id: 'active',
      header: 'Active',
      cell: (row) => (
        <UzSwitch
          checked={row.isActive}
          disabled={updateChannel.isPending}
          onCheckedChange={(checked) =>
            updateChannel.mutate({ id: row.id, payload: { isActive: checked } })
          }
        />
      ),
      width: '90px',
    },
    {
      id: 'config',
      header: '',
      cell: (row) => (
        <div className="flex justify-end">
          <UzButton variant="outline" size="sm" onClick={() => setConfiguring(row)}>
            <Settings2 className="mr-1.5 h-3.5 w-3.5" />
            Configure
          </UzButton>
        </div>
      ),
      width: '140px',
      align: 'right',
    },
  ];

  return (
    <>
      <UzTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        loading={channelsQuery.isLoading}
      />
      {configuring && (
        <ChannelConfigDrawer channel={configuring} onClose={() => setConfiguring(null)} />
      )}
    </>
  );
};

/**
 * Tiny inline editor for the per-channel rate limit. Local state mirrors
 * the row's `rateLimitPerSec`; commits on blur via `useUpdateNotificationChannel`
 * so the operator can type freely without triggering a network round-trip
 * per keystroke.
 */
const RateCell = ({ row }: { row: NotificationChannelItem }): React.JSX.Element => {
  const [value, setValue] = useState<number | null>(row.rateLimitPerSec);
  const update = useUpdateNotificationChannel();

  React.useEffect(() => {
    setValue(row.rateLimitPerSec);
  }, [row.rateLimitPerSec]);

  const commit = (): void => {
    if (value === null || value === row.rateLimitPerSec) return;
    update.mutate({ id: row.id, payload: { rateLimitPerSec: value } });
  };

  return (
    <UzNumberInput
      value={value ?? ''}
      onValueChange={setValue}
      onBlur={commit}
      min={1}
      max={10_000}
      step={1}
      precision={0}
      disabled={update.isPending}
    />
  );
};
